import { useState } from 'react';
import { Loader2, Phone } from 'lucide-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { adminFetch } from '@/hooks/admin/useAdminApi';
import type { VoiceVerificationMode } from '@/hooks/admin/useCompanySettings';

// Its own query key, NOT ['admin','company-settings']: refetching that one
// re-seeds the page's unsaved Billing-emails / Fees / Desk forms.
const KEY = ['admin', 'voice-verification-mode'];

export function useVoiceMode() {
  return useQuery({
    queryKey: KEY,
    queryFn: async () => {
      const res = await adminFetch<{ ok: true; settings: { voice_verification_mode?: VoiceVerificationMode } }>(
        'admin-company-settings',
      );
      return res.settings.voice_verification_mode ?? 'off';
    },
  });
}

function useSetVoiceMode() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: VoiceVerificationMode) =>
      adminFetch<{ ok: true; settings: { voice_verification_mode?: VoiceVerificationMode } }>('admin-company-settings', {
        method: 'PUT',
        body: JSON.stringify({ voice_verification_mode: v }),
      }),
    onSuccess: (res) => qc.setQueryData(KEY, res.settings?.voice_verification_mode ?? 'off'),
  });
}

const OPTIONS: Array<{ value: VoiceVerificationMode; label: string; desc: string }> = [
  {
    value: 'off',
    label: 'Off',
    desc: 'As before: a caller ID or a phone number on file pulls up the account with no code, and a staff phone opens the staff tools.',
  },
  {
    value: 'email',
    label: 'Email a code',
    desc: 'Before any account detail, order or staff tool, the caller reads back a code emailed to the address on file — never texted.',
  },
  {
    value: 'sms',
    label: 'Text a code',
    desc: 'The code is texted to a number on file; an account with no textable number, or texting not set up, gets the emailed code instead.',
  },
];

/**
 * Phone agent caller verification — orders.company_settings.
 * voice_verification_mode. Acts straight away (not part of the page's Save
 * button); the phone agent picks it up within a minute. Superadmin only: the
 * endpoint behind it refuses anyone else.
 */
export function VoiceVerificationCard() {
  const { data, isLoading, error } = useVoiceMode();
  const update = useSetVoiceMode();
  const [pending, setPending] = useState<VoiceVerificationMode | null>(null);

  if (isLoading) return null;
  if (error || !data) {
    return (
      <div className="rounded-lg border border-red-300 bg-red-50 p-4 text-sm text-red-800">
        Could not read the phone agent setting: {error instanceof Error ? error.message : 'unknown error'}.
      </div>
    );
  }

  const current: VoiceVerificationMode = data;

  function choose(v: VoiceVerificationMode) {
    if (v === current || update.isPending) return;
    setPending(v);
    update.mutate(v, { onSettled: () => setPending(null) });
  }

  return (
    <div className="rounded-xl border border-brix-border bg-brix-surface p-5 space-y-3">
      <div className="flex items-start gap-3">
        <Phone size={20} className="text-brix-muted mt-0.5" aria-hidden />
        <div className="flex-1">
          <h2 className="text-base font-semibold text-brix-ink">Phone agent caller verification</h2>
          <p className="text-xs leading-relaxed text-brix-muted mt-0.5">
            How the phone agent (Chloe / Ziggy) proves who is calling before it reads out an account,
            takes an order or opens staff tools. Saves straight away; live calls pick it up within a minute.
          </p>
        </div>
      </div>
      <div role="radiogroup" aria-label="Phone agent caller verification" className="space-y-2">
        {OPTIONS.map((o) => {
          const on = o.value === current;
          return (
            <label
              key={o.value}
              className={
                on
                  ? 'flex cursor-pointer items-start gap-3 rounded-lg border-2 border-brix-primary bg-white p-3'
                  : 'flex cursor-pointer items-start gap-3 rounded-lg border border-brix-border bg-white p-3'
              }
            >
              <input
                type="radio"
                name="voice_verification_mode"
                className="mt-1"
                checked={on}
                disabled={update.isPending}
                onChange={() => choose(o.value)}
              />
              <span>
                <span className="block text-sm font-semibold text-brix-ink">
                  {o.label}
                  {pending === o.value && <Loader2 size={12} className="ml-2 inline animate-spin" />}
                </span>
                <span className="block text-xs text-brix-muted">{o.desc}</span>
              </span>
            </label>
          );
        })}
      </div>
      {update.isError && (
        <p className="text-sm text-red-700">{update.error instanceof Error ? update.error.message : 'Save failed'}</p>
      )}
    </div>
  );
}
